import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, map, of, switchMap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';

import { SupplierService } from '../../services/supplier/supplier.service';
import { ManageSurveyApiActions } from '../manage-survey/manage-survey.actions';
import { SupplierActions, SupplierApiActions } from './supplier.actions';

@Injectable()
export class SupplierEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _supplierService = inject(SupplierService);

  // A CPI edit or a child-survey switch changes what the allocations look like,
  // so both refetch alongside the explicit load.
  public loadAllocations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SupplierActions.loadAllocations,
        ManageSurveyApiActions.updateCurrentCpiSuccess,
        ManageSurveyApiActions.updateChildCurrentCpiSuccess,
        ManageSurveyApiActions.loadChildSurveySuccess,
      ),
      map((action) =>
        action.type === SupplierActions.loadAllocations.type
          ? { surveyId: action.surveyId, params: action.params }
          : {
              surveyId: action.survey.id,
              params: { scope: 'view' as const },
            },
      ),
      switchMap(({ surveyId, params }) =>
        this._supplierService.allocations(surveyId, params).pipe(
          map((allocations) =>
            SupplierApiActions.loadAllocationsSuccess({ allocations }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.loadAllocationsFailure({
                  error: err.message ?? 'Failed to load supplier allocations',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadBlends$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.loadBlends),
      switchMap(({ locale }) =>
        this._supplierService.blends(locale).pipe(
          map((blends) => SupplierApiActions.loadBlendsSuccess({ blends })),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.loadBlendsFailure({
                  error: err.message ?? 'Failed to load supplier blends',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public loadFlexibilityPriceParams$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.loadFlexibilityPriceParams),
      switchMap(({ surveyId }) =>
        this._supplierService.flexibilityPriceParams(surveyId).pipe(
          map((params) =>
            SupplierApiActions.loadFlexibilityPriceParamsSuccess({ params }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.loadFlexibilityPriceParamsFailure({
                  error:
                    err.message ?? 'Failed to load flexibility price params',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // concatMap, not switchMap: an in-flight save must never be cancelled by a
  // second Apply — the user would be told it succeeded while it was dropped.
  public updateAllocations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierActions.updateAllocations),
      concatMap(({ surveyId, update }) =>
        this._supplierService.updateAllocations(surveyId, update).pipe(
          map((allocations) =>
            SupplierApiActions.updateAllocationsSuccess({ allocations }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SupplierApiActions.updateAllocationsFailure({
                  error: err.message ?? 'Failed to update suppliers',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );
}
